'use client'

import React, { useState, useRef, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Textarea } from '@/components/ui/textarea'
import { useToast } from '@/hooks/use-toast'
import { MessageCircle, Send, Upload, FileText, DollarSign, AlertTriangle, CheckCircle, X, Minimize2, Maximize2, Image, File, Camera, RotateCcw, Globe } from 'lucide-react'
import { useChatbot } from '@/context/chatbot-context'
import { useSession } from 'next-auth/react'
import { usePathname } from 'next/navigation'

interface TransactionAnalysis {
  matchedTransactions: number
  unmatchedRows: number
  totalExpectedPayout: number
  discrepancies: Array<{
    type: string
    description: string
    severity: 'low' | 'medium' | 'high'
  }>
  recommendations: string[]
}

export default function GlobalAIChatbot() {
  const { data: session } = useSession()
  const pathname = usePathname()
  const { toast } = useToast()
  
  const {
    isOpen,
    setIsOpen,
    isMinimized,
    setIsMinimized,
    messages,
    addMessage,
    merchantId,
    setMerchantId,
    csvData,
    setCsvData,
    analysisResult,
    setAnalysisResult,
    uploadedFile,
    setUploadedFile,
    isLoading,
    setIsLoading,
    clearConversation
  } = useChatbot()

  const [inputValue, setInputValue] = useState('')
  const [dragActive, setDragActive] = useState(false)
  const [overlayOpen, setOverlayOpen] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  // Hide the floating button while a modal/dialog is open — inline modals render
  // in a trapped stacking context, so this body-level FAB would otherwise cover
  // their primary action (e.g. "Create Merchant") on mobile.
  useEffect(() => {
    if (typeof document === 'undefined') return
    const check = () => {
      const dialog = document.querySelector('[role="dialog"]')
      const inlineModal = Array.from(document.querySelectorAll('div.fixed.inset-0')).some((el) => {
        const s = getComputedStyle(el as HTMLElement)
        // A real modal is interactive (pointer-events on) and stacks above the
        // page (z >= 40). Excludes decorative pointer-events-none z-0 backgrounds.
        return (
          s.display !== 'none' &&
          s.visibility !== 'hidden' &&
          s.pointerEvents !== 'none' &&
          parseInt(s.zIndex || '0', 10) >= 40 &&
          (el as HTMLElement).offsetHeight > 0
        )
      })
      setOverlayOpen(!!dialog || inlineModal)
    }
    check()
    const mo = new MutationObserver(check)
    mo.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style'] })
    return () => mo.disconnect()
  }, [])

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  // All hooks must be called before any conditional returns
  useEffect(() => {
    scrollToBottom()
  }, [messages])

  // Only show for admin users
  if (!session?.user || session.user.role !== 'ADMIN') {
    return null
  }

  // Hide on auth pages
  if (pathname?.includes('/auth/') || pathname?.includes('/signin') || pathname?.includes('/signup')) {
    return null
  }

  // Don't let the FAB overlap an open modal's actions.
  if (overlayOpen) {
    return null
  }

  const handleFileSelect = (file: File) => {
    // Validate file type and size
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'text/csv', 'application/csv', 'text/plain', 'application/pdf']
    const maxSize = 10 * 1024 * 1024 // 10MB
    
    if (!validTypes.includes(file.type)) {
      toast({
        title: "Invalid File Type",
        description: "Please upload CSV, images (JPG, PNG, GIF), or PDF files only",
        variant: "destructive"
      })
      return
    }
    
    if (file.size > maxSize) {
      toast({
        title: "File Too Large",
        description: "Please upload files smaller than 10MB",
        variant: "destructive"
      })
      return
    }
    
    setUploadedFile(file)
    setCsvData('') // Clear manual CSV input when file is uploaded
    
    toast({
      title: "File Ready",
      description: `${file.name} is ready for analysis`,
    })
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragActive(false)
    
    const files = Array.from(e.dataTransfer.files)
    if (files.length > 0) {
      handleFileSelect(files[0])
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setDragActive(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setDragActive(false)
  }

  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => {
        if (typeof reader.result === 'string') {
          resolve(reader.result)
        } else {
          reject(new Error('Failed to convert file to base64'))
        }
      }
      reader.onerror = () => reject(reader.error)
      reader.readAsDataURL(file)
    })
  }

  const handleFileAnalysis = async (file: File, message: string) => {
    setIsLoading(true)

    try {
      const fileBase64 = await convertFileToBase64(file)
      
      addMessage('user', `📁 **Analyzing file: ${file.name}**\n\n${message}\n\n🤖 **AI Analysis in progress...**`)

      const response = await fetch('/api/admin/chatbot/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: message,
          fileData: fileBase64,
          fileName: file.name,
          fileType: file.type,
          context: {
            messageHistory: messages.slice(-5)
          }
        })
      })

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.error || 'File analysis failed')
      }

      addMessage('assistant', result.response, { 
        analysis: result.analysis,
        extractedData: result.extractedData
      })

      // Clear file after analysis
      setUploadedFile(null)

    } catch (error) {
      console.error('File analysis error:', error)
      addMessage('assistant', `❌ **File Analysis Failed**\n\n${error instanceof Error ? error.message : 'Unknown error occurred'}`)
    } finally {
      setIsLoading(false)
    }
  }

  const handleCreateSettlement = async (analysis: TransactionAnalysis) => {
    if (!merchantId) return

    setIsLoading(true)
    addMessage('user', `💰 **Creating settlement for Merchant ID: ${merchantId}**`)

    try {
      const response = await fetch('/api/admin/chatbot/create-settlement', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          merchantId: parseInt(merchantId),
          analysis: analysis
        })
      })

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.error || 'Settlement creation failed')
      }

      addMessage('assistant', result.response, { settlement: result.settlement })

    } catch (error) {
      console.error('Settlement creation error:', error)
      addMessage('assistant', `❌ **Settlement Creation Failed**\n\n${error instanceof Error ? error.message : 'Unknown error occurred'}`)
    } finally {
      setIsLoading(false)
    }
  }

  const handleSendMessage = async () => {
    console.log('🚀 handleSendMessage called, inputValue:', inputValue)
    
    if (!inputValue.trim() && !uploadedFile) {
      console.log('❌ No input value or file, returning')
      return
    }

    const userMessage = inputValue.trim()
    console.log('📤 Sending message:', userMessage)
    
    // If there's a file uploaded, handle it through file analysis
    if (uploadedFile) {
      if (!userMessage) {
        toast({
          title: "Please add a message",
          description: "Describe what you want to do with the uploaded file",
          variant: "destructive"
        })
        return
      }
      
      setInputValue('')
      await handleFileAnalysis(uploadedFile, userMessage)
      return
    }

    setInputValue('')
    setIsLoading(true)

    addMessage('user', userMessage)

    try {
      const response = await fetch('/api/admin/chatbot/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMessage,
          context: {
            hasAnalysis: !!analysisResult,
            messageHistory: messages.slice(-5) // Send last 5 messages for context
          }
        })
      })

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.error || 'Chat request failed')
      }

      addMessage('assistant', result.response)

    } catch (error) {
      console.error('Chat error:', error)
      addMessage('assistant', `❌ **Error**\n\n${error instanceof Error ? error.message : 'Unknown error occurred'}`)
    } finally {
      setIsLoading(false)
    }
  }

  // Helper function to detect if CSV field contains a question
  const detectQuestionInCSV = (text: string): boolean => {
    const questionWords = ['how', 'what', 'when', 'where', 'why', 'who', 'which', 'do', 'does', 'is', 'are', 'can', 'will', 'should']
    const settlementWords = ['settlement', 'money', 'payment', 'send', 'pay', 'amount', 'payout', 'transaction']
    const lowerText = text.toLowerCase()
    
    // Must contain at least one question word
    const hasQuestionWord = questionWords.some(word => lowerText.includes(word))
    
    // Should not look like CSV data (no commas, or if commas exist, should have question indicators)
    const isNotCSV = !text.includes(',') || text.includes('?') || lowerText.includes('@')
    
    // Should contain settlement/payment related terms
    const hasSettlementTerms = settlementWords.some(word => lowerText.includes(word))
    
    // Text should be reasonably long for a question
    const isReasonableLength = text.length > 15
    
    console.log('🔍 Question Detection Debug:', {
      text: text.substring(0, 50) + '...',
      hasQuestionWord,
      isNotCSV,
      hasSettlementTerms,
      isReasonableLength,
      result: hasQuestionWord && isNotCSV && (hasSettlementTerms || text.includes('?')) && isReasonableLength
    })
    
    return hasQuestionWord && isNotCSV && (hasSettlementTerms || text.includes('?')) && isReasonableLength
  }

  const formatContent = (content: string) => {
    return content
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/```([\s\S]*?)```/g, '<pre class="bg-muted p-2 rounded text-sm overflow-x-auto"><code>$1</code></pre>')
      .replace(/`(.*?)`/g, '<code class="bg-muted px-1 rounded text-sm">$1</code>')
      .replace(/\n/g, '<br>')
  }

  const getSeverityColor = (severity: 'low' | 'medium' | 'high') => {
    switch (severity) {
      case 'low': return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300'
      case 'medium': return 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300'
      case 'high': return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
      default: return 'bg-muted text-muted-foreground'
    }
  }

  // Floating button when minimized
  if (!isOpen) {
    return (
      <div className="fixed bottom-6 right-6 z-50">
        <Button
          onClick={() => setIsOpen(true)}
          className="h-14 w-14 rounded-full shadow-lg bg-blue-600 hover:bg-blue-700 text-white"
          title="Open AI Assistant"
        >
          <MessageCircle className="h-6 w-6" />
        </Button>
        {/* Notification badge for unread messages */}
        {messages.length > 1 && (
          <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-6 w-6 flex items-center justify-center">
            {Math.min(messages.length - 1, 9)}
          </div>
        )}
      </div>
    )
  }

  // Full chatbot interface
  return (
    <div className="fixed bottom-6 left-6 z-40">
      <Card className={`w-96 shadow-2xl border-0 transition-all duration-300 ${isMinimized ? 'h-16' : 'h-[600px]'}`}>
        {/* Header */}
        <CardHeader className="p-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <MessageCircle className="h-5 w-5" />
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                AI Transaction Assistant
                <div title="Global Assistant">
                  <Globe className="h-3 w-3 opacity-70" />
                </div>
              </CardTitle>
            </div>
            <div className="flex items-center space-x-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={clearConversation}
                className="h-6 w-6 p-0 text-white hover:bg-white/20"
                title="Clear conversation"
              >
                <RotateCcw className="h-3 w-3" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsMinimized(!isMinimized)}
                className="h-6 w-6 p-0 text-white hover:bg-white/20"
              >
                {isMinimized ? <Maximize2 className="h-4 w-4" /> : <Minimize2 className="h-4 w-4" />}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsOpen(false)}
                className="h-6 w-6 p-0 text-white hover:bg-white/20"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>

        {!isMinimized && (
          <>
            {/* File Upload Area */}
            <div className="p-3 border-b bg-muted/50">
              <div 
                className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
                  dragActive
                    ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20'
                    : uploadedFile
                    ? 'border-green-400 bg-green-50 dark:bg-green-900/20'
                    : 'border-border hover:border-border/80'
                }`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
              >
                {uploadedFile ? (
                  <div className="space-y-2">
                    <div className="flex items-center justify-center space-x-2 text-green-600">
                      {uploadedFile.type.includes('image') ? <Image className="h-4 w-4" /> : <File className="h-4 w-4" />}
                      <span className="text-sm font-medium">{uploadedFile.name}</span>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {uploadedFile.type} • {(uploadedFile.size / 1024).toFixed(1)} KB
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setUploadedFile(null)}
                      className="h-6 text-xs"
                    >
                      <X className="h-3 w-3 mr-1" />
                      Remove
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex justify-center space-x-2">
                      <Upload className="h-5 w-5 text-muted-foreground" />
                      <Camera className="h-5 w-5 text-muted-foreground" />
                      <FileText className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Drop transaction files here or{' '}
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="text-primary hover:text-primary/80 font-medium"
                      >
                        browse
                      </button>
                    </div>
                    <div className="text-xs text-muted-foreground/70">
                      CSV, Images, PDFs up to 10MB
                    </div>
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  accept=".csv,.txt,.pdf,.jpg,.jpeg,.png,.gif"
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (file) handleFileSelect(file)
                  }}
                />
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-3 space-y-3 max-h-96">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] p-3 rounded-lg text-sm ${
                      message.type === 'user'
                        ? 'bg-blue-600 text-white'
                        : message.type === 'system'
                        ? 'bg-muted text-foreground border border-border'
                        : 'bg-muted/50 text-foreground'
                    }`}
                  >
                    <div
                      className="text-sm"
                      dangerouslySetInnerHTML={{ __html: formatContent(message.content) }}
                    />
                    
                    {/* Extracted Data Info */}
                    {message.data?.extractedData && (
                      <div className="mt-3 p-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded">
                        <div className="text-sm font-medium text-blue-800 dark:text-blue-300">🤖 AI Extraction Results</div>
                        <div className="text-xs text-blue-600 dark:text-blue-400 mt-1 grid grid-cols-2 gap-1">
                          <div>Source: {message.data.extractedData.source}</div>
                          <div>Confidence: {(message.data.extractedData.extractionConfidence * 100).toFixed(1)}%</div>
                          <div>Transactions: {message.data.extractedData.transactionCount}</div>
                          <div>Status: {message.data.extractedData.rawData}</div>
                        </div>
                      </div>
                    )}

                    {/* Analysis Results */}
                    {message.data?.analysis && (
                      <div className="mt-3 space-y-2">
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div className="bg-card p-2 rounded">
                            <div className="font-medium text-green-600 dark:text-green-400">✅ Matched</div>
                            <div className="text-lg font-bold text-foreground">{message.data.analysis.matchedTransactions}</div>
                          </div>
                          <div className="bg-card p-2 rounded">
                            <div className="font-medium text-orange-600 dark:text-orange-400">⚠️ Unmatched</div>
                            <div className="text-lg font-bold text-foreground">{message.data.analysis.unmatchedRows}</div>
                          </div>
                        </div>

                        <div className="bg-card p-2 rounded">
                          <div className="font-medium text-blue-600 dark:text-blue-400">💰 Expected Payout</div>
                          <div className="text-lg font-bold text-foreground">${message.data.analysis.totalExpectedPayout.toFixed(2)}</div>
                        </div>

                        {message.data.analysis.discrepancies.length > 0 && (
                          <div className="space-y-1">
                            <div className="font-medium text-red-600 dark:text-red-400">🚨 Discrepancies</div>
                            {message.data.analysis.discrepancies.slice(0, 3).map((disc: any, idx: number) => (
                              <div key={idx} className={`text-xs p-1 rounded ${getSeverityColor(disc.severity)}`}>
                                {disc.description}
                              </div>
                            ))}
                            {message.data.analysis.discrepancies.length > 3 && (
                              <div className="text-xs text-muted-foreground">
                                +{message.data.analysis.discrepancies.length - 3} more discrepancies
                              </div>
                            )}
                          </div>
                        )}

                        <Button 
                          onClick={() => message.data?.analysis && handleCreateSettlement(message.data.analysis)}
                          disabled={isLoading || !message.data?.analysis}
                          size="sm"
                          className="w-full mt-2"
                        >
                          <DollarSign className="h-4 w-4 mr-2" />
                          Create Settlement
                        </Button>
                      </div>
                    )}

                    {/* Settlement Results */}
                    {message.data?.settlement && (
                      <div className="mt-3 p-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded">
                        <div className="text-sm font-medium text-green-800 dark:text-green-300">✅ Settlement Created</div>
                        <div className="text-xs text-green-600 dark:text-green-400 mt-1">
                          ID: {message.data.settlement.id} • Amount: ${message.data.settlement.amount}
                        </div>
                      </div>
                    )}

                    <div className="text-xs opacity-70 mt-2">
                      {message.timestamp.toLocaleTimeString()}
                    </div>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Chat Input */}
            <div className="p-3 border-t bg-card">
              <div className="flex space-x-2">
                <Input
                  placeholder="Ask anything: How much to send to perfume.team.experts@gmail.com for latest settlement?"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault()
                      handleSendMessage()
                    }
                  }}
                  disabled={isLoading}
                  className="flex-1 text-sm"
                />
                <Button 
                  onClick={handleSendMessage}
                  disabled={isLoading || !inputValue.trim()}
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                💡 I can help with settlements, transactions, and analysis. Drop files above for advanced analysis.
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  )
}
